import {
  ApiChannel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Grid, Portal, Stack, Typography } from "@mui/material";
import { channelService } from "Apis";
import { TypographyHelp } from "Components/UI/TypographyHelp";
import {
  AnalyticsStatCard,
  NOT_SET,
  AnalyticsChartStatCard,
  TrafficTable,
} from "features/analytics";
import { useChannels } from "Hooks/useChannels";
import { useSelectedProject } from "Hooks/useSelectedProject";
import produce from "immer";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { StatCardTypography } from "Components/UI/StatCard";
import { useConfirmation } from "Hooks/useConfirmation";
import { ChannelsList } from "features/channels";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import { OpenInNew } from "@mui/icons-material";

export const ChannelsPage = () => {
  const { selectedProject } = useSelectedProject();
  const { channels, setChannels } = useChannels();
  const history = useHistory();
  const handleDelete = useConfirmation();
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [detailsDrawerAttributes, setDetailsDrawerAttributes] = useState<
    ApiChartAttribute[]
  >([]);

  const newChannel = () => {
    history.push(`channels/new`);
  };

  const editChannel = (channel: ApiChannel) => {
    history.push(`channels/${channel.id}`);
  };

  const deleteChannel = (channel: ApiChannel) => {
    handleDelete({
      title: `Are you sure you want to delete this channel`,
      body: `This action can not be undone`,
      callback: async () => {
        await channelService.channelServiceDeleteChannel({
          projectId: selectedProject?.id as number,
          channelId: channel.id as number,
        });

        setChannels(
          produce(channels, (draft) => {
            const idx = channels.findIndex((ch) => ch.id === channel.id);
            if (idx !== -1) draft.splice(idx, 1);
          })
        );

        return `${channel.name || ""} Deleted`;
      },
    });
  };

  const onTrafficDrawerClose = useCallback(() => {
    setOpenDetailsDrawer(false);
    setDetailsDrawerAttributes([]);
  }, []);

  return (
    <>
      {selectedProject ? (
        <>
          <Portal container={() => document.getElementById("pageTitle")}>
            <TypographyHelp
              variant="h1"
              text={`${selectedProject.name} Channels`}
              placement={"right"}
              tooltip={`
                Channels group traffic into buckets based on rules so that we can do performance analysis in analytics at a top level. 
                They follow a cascading priority, meaning that if a pageview matches a rule set with priority 2 and another rule set with priority 5, 
                the rule set with priority 2 will take precedent.`}
            />
          </Portal>
          <Grid container spacing={4}>
            <Grid item xs={9}>
              <ChannelsList
                onEdit={editChannel}
                onDelete={deleteChannel}
                project={selectedProject}
                channels={channels || []}
                setChannels={setChannels}
                onClick={newChannel}
              />
            </Grid>
            <Grid item xs={3}>
              <Stack spacing={4}>
                <AnalyticsCard
                  overrideDate={{
                    lastNDays: 365,
                    from: undefined,
                    to: undefined,
                  }}
                  height={278}
                  chart={{
                    name: "Unmatched Sources In The Last Year",
                    dimension: [ApiDimension.DIMENSION_SOURCE],
                    template: ApiChartTemplate.SNAPSHOT,
                    type: ApiChartType.PIE,
                    includeEmpty: true,
                    attributes: [
                      {
                        key: ApiDimension.DIMENSION_CHANNEL,
                        value: NOT_SET,
                      },
                    ],
                    yAxisKeys: [
                      {
                        key: "l",
                        fields: [ApiMetric.METRIC_LEADS_COUNT],
                      },
                    ],
                    displayOptions: {
                      noFilters: true,
                      showTotals: true,
                    },
                  }}
                />

                <AnalyticsStatCard
                  chartOutput={{
                    lastNDays: 365,
                    from: undefined,
                    to: undefined,
                  }}
                  chart={{
                    name: "",
                    dimension: [ApiDimension.DIMENSION_SOURCE],
                    template: ApiChartTemplate.SNAPSHOT,
                    type: ApiChartType.GRAPH,
                    attributes: [
                      {
                        key: ApiDimension.DIMENSION_CHANNEL,
                        value: NOT_SET,
                      },
                    ],
                    yAxisKeys: [
                      {
                        key: "l",
                        fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                      },
                    ],
                  }}
                  renderBody={(data) => (
                    <>
                      <StatCardTypography size="xs" mb={1}>
                        Unmatched Source With Most Sessions
                      </StatCardTypography>
                      <StatCardTypography size="medium" lines={1}>
                        {data[0]?.name}
                      </StatCardTypography>
                    </>
                  )}
                />
                <AnalyticsStatCard
                  chartOutput={{
                    lastNDays: 365,
                    from: undefined,
                    to: undefined,
                  }}
                  chart={{
                    name: "",
                    dimension: [ApiDimension.DIMENSION_MEDIUM],
                    template: ApiChartTemplate.SNAPSHOT,
                    type: ApiChartType.GRAPH,
                    attributes: [
                      {
                        key: ApiDimension.DIMENSION_CHANNEL,
                        value: NOT_SET,
                      },
                    ],
                    yAxisKeys: [
                      {
                        key: "l",
                        fields: [ApiMetric.METRIC_LEADS_COUNT],
                      },
                    ],
                  }}
                  renderBody={(data) => (
                    <>
                      <StatCardTypography size="xs">
                        Unmatched Medium With Most Sessions
                      </StatCardTypography>
                      <StatCardTypography mt={1} size="medium" lines={1}>
                        {data[0]?.name}
                      </StatCardTypography>
                    </>
                  )}
                />

                <AnalyticsChartStatCard
                  chartOutput={{
                    lastNDays: 365,
                    from: undefined,
                    to: undefined,
                  }}
                  chart={{
                    name: "Number of Visits not in a channel in the last year",
                    dimension: [ApiDimension.DIMENSION_NOT_SET],
                    template: ApiChartTemplate.SNAPSHOT,
                    type: ApiChartType.GRAPH,
                    comparison: true,
                    attributes: [
                      {
                        key: ApiDimension.DIMENSION_CHANNEL,
                        value: NOT_SET,
                      },
                    ],
                    displayOptions: {
                      statTitle:
                        "Number of Visits not in a channel in the last year",
                    },
                    yAxisKeys: [
                      {
                        key: "l",
                        fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                      },
                    ],
                  }}
                  noToolbar
                />
              </Stack>
            </Grid>
            <Grid item xs={7} sx={{ minHeight: 445 }}>
              <AnalyticsCard
                overrideDate={{
                  lastNDays: 365,
                  from: undefined,
                  to: undefined,
                }}
                onClick={{
                  [ApiMetric.METRIC_SESSIONS_COUNT]: [
                    {
                      text: "View These Visits",
                      icon: <OpenInNew />,
                      fn: (_, f) => {
                        if (!f) {
                          return;
                        }
                        setDetailsDrawerAttributes(f);
                        setOpenDetailsDrawer(true);
                      },
                    },
                  ],
                }}
                chart={{
                  name: "Matched Channels in Last Year",
                  dimension: [
                    ApiDimension.DIMENSION_CHANNEL,
                    ApiDimension.DIMENSION_SOURCE,
                    ApiDimension.DIMENSION_MEDIUM,
                  ],
                  template: ApiChartTemplate.TABLE,
                  type: ApiChartType.TABLE,
                  orderBy: `${ApiDimension.DIMENSION_CHANNEL} asc`,
                  includeEmpty: true,
                  attributes: [],
                  yAxisKeys: [
                    {
                      key: "l",
                      fields: [
                        ApiMetric.METRIC_LEADS_COUNT,
                        ApiMetric.METRIC_SESSIONS_COUNT,
                      ],
                    },
                  ],
                  having: [
                    {
                      metric: ApiMetric.METRIC_SESSIONS_COUNT,
                      operator: ApiOperator.IS_GREATER_THAN,
                      value: 0,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={5} sx={{ minHeight: 445 }}>
              <AnalyticsCard
                overrideDate={{
                  lastNDays: 365,
                  from: undefined,
                  to: undefined,
                }}
                chart={{
                  name: "Matched Channels in Last Year (trended)",
                  dimension: [
                    ApiDimension.DIMENSION_SESSION_START_BY_MONTH,
                    ApiDimension.DIMENSION_CHANNEL,
                  ],
                  template: ApiChartTemplate.TREND,
                  type: ApiChartType.GRAPH,
                  includeEmpty: true,
                  attributes: [],
                  yAxisKeys: [
                    {
                      key: "l",
                      stackId: "l",
                      fields: [ApiMetric.METRIC_LEADS_COUNT],
                      chart: {
                        [ApiMetric.METRIC_LEADS_COUNT]: ApiYAxisChartType.BAR,
                      },
                    },
                  ],
                  having: [
                    {
                      metric: ApiMetric.METRIC_SESSIONS_COUNT,
                      operator: ApiOperator.IS_GREATER_THAN,
                      value: 0,
                    },
                  ],
                  displayOptions: {
                    noFilters: true,
                  },
                }}
              />
            </Grid>
          </Grid>
        </>
      ) : (
        "No Project selected"
      )}
      <StyledDrawer
        anchor="right"
        open={openDetailsDrawer}
        onClose={onTrafficDrawerClose}
        hideDivider
      >
        <StyledDrawerTitle>
          <Typography variant="subtitle1">Explore Traffic</Typography>
          <Typography variant="body2">
            Explore Traffic from these channels over the last year (limited to
            most recent 1000 records)
          </Typography>
        </StyledDrawerTitle>
        <StyledDrawerContainer sx={{ minWidth: "85vw" }}>
          <TrafficTable
            attributes={detailsDrawerAttributes}
            dateRange={{ lastNDays: 365, from: undefined, to: undefined }}
            limit={1000}
          />
        </StyledDrawerContainer>
      </StyledDrawer>
    </>
  );
};
